import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Select, TreeSelect, Row, Col,
} from 'antd';
import { DeleteOutlined, DragOutlined } from '@ant-design/icons';
import { Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import BorderlessButton from '../../common/buttons/BorderlessButton';

import {
  getTimecardFields,
  getFormFields,
  getBoardCardFields,
  EXPORTABLE_FIELD_TYPES,
  FIELDS_WITH_FILTERS,
  FIELDS_WITH_TEXT,
  FIELDS_WITH_CONFIG,
  CONFIG_MAP,
  FIRST_COST_CODE_KEY,
  MULTI_EXPORTABLE_FIELD_TYPES,
  TIME_TRACKING_GRID_EXPORT_COLUMNS,
  getExportFieldType,
} from './exports.constants';

import colors from '../../constants/Colors';
import { getFieldMap } from '../../forms/formHelpers';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import useProjectOrGlobalCostcodes from '../../costcodes/useProjectOrGlobalCostcodes';

const GET_FIELD_FUNCTIONS = {
  timeEntries: getTimecardFields,
  forms: getFormFields,
  boardCards: getBoardCardFields,
};

function ExportsColumn({
  column: {
    id,
    title,
    fields = [],
    field,
    filterValue,
    isMulti,
    subTotal,
  } = {},
  divisionId,
  orderIndex,
  onChange,
  onDelete,
  tableFieldId,
  timeTrackingGridFieldId,
  hasEclipse,
  type,
  formTemplateId,
  boardCardTemplateId,
  hasSummarize,
}) {
  const { t } = useTranslation();
  const customFields = useSelector((state) => state.timecards.customFields);
  const formTemplates = useSelector((state) => state.forms.templates);
  const boardCardTemplates = useSelector((state) => state.boards.cardTemplates);
  const userCustomFields = useSelector((state) => state.users.customFieldTemplate);

  const needsCostcodeOptions = !isMulti && field === 'costcodes.hours';
  const { costcodes: globalCostcodes } = useProjectOrGlobalCostcodes({
    projectId: null,
    divisionId,
    visible: needsCostcodeOptions,
  });

  const TIMECARD_FIELDS = type in GET_FIELD_FUNCTIONS
    ? GET_FIELD_FUNCTIONS[type](t)
    : [];

  const onDeleteClicked = useCallback(() => {
    onDelete(id);
  }, [onDelete, id]);

  const onColumnTitleChanged = useCallback((e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    onChange(id, { title: value });
  }, [onChange, id]);

  const onDataChange = useCallback((key) => (value) => {
    onChange(id, { [key]: value });
  }, [onChange, id]);

  const onFieldsChange = useCallback((newValue) => {
    const selectedIds = Array.isArray(newValue) ? newValue : [newValue];
    onDataChange('fields')(selectedIds);
  }, [onDataChange]);

  const onTextChanged = useCallback((e) => {
    onDataChange('filterValue')(e?.target?.value);
  }, [onDataChange]);

  const onMultiChange = useCallback((checked) => {
    const change = {
      isMulti: checked,
    };
    if (checked) {
      change.field = null;
    } else {
      change.fields = [];
    }
    onChange(id, change);
  }, [onChange, id]);

  const ourFormTemplate = useMemo(() => {
    if (!formTemplateId) return null;
    const {
      [formTemplateId]: {
        jsonFormData,
      } = {},
    } = formTemplates;
    let parsed;
    try {
      parsed = JSON.parse(jsonFormData);
    } catch (err) {
      //
    }
    return parsed;
  }, [formTemplates, formTemplateId, type]);

  const ourCustomSections = useMemo(() => {
    if (type === 'forms') {
      return ourFormTemplate?.sections ?? [];
    }
    if (type === 'boardCards') {
      return boardCardTemplates?.[boardCardTemplateId]?.fields ?? [];
    }
    const {
      [divisionId]: { fields: divCustomFields = [] } = {},
    } = customFields;
    return divCustomFields;
  }, [ourFormTemplate, customFields, divisionId, type, boardCardTemplateId]);

  const ourFieldMap = useMemo(() => getFieldMap(ourCustomSections, 'id'), [ourCustomSections]);

  const getExportableCustomFields = useCallback((sections) => {
    let exportableTypes = EXPORTABLE_FIELD_TYPES;
    if (isMulti) {
      let selectedField = fields[0];
      if (selectedField?.endsWith('-lat') || selectedField?.endsWith('-lng')) {
        // Remove trailing
        const parts = selectedField.split('-');
        parts.pop();
        selectedField = parts.join('-');
      }
      exportableTypes = fields.length
        ? new Set([getExportFieldType(ourFieldMap[selectedField])])
        : MULTI_EXPORTABLE_FIELD_TYPES;
    }

    const exportableCustomFields = sections.reduce((acc, section) => {
      const { fields: sectionFields = [], id: sectionId } = section;
      const exportableFields = sectionFields.reduce((fieldAcc, formField) => {
        const { configProps, id: fieldId } = formField;
        const effectiveType = getExportFieldType(formField);
        const canExport = effectiveType
          && exportableTypes.has(effectiveType)
          && configProps
          && configProps.title
        if (!canExport) return fieldAcc;
        if (effectiveType === 'gpsLocation') {
          const firstFieldIsLat = fields[0]?.endsWith('-lat');
          const firstFieldIsLng = fields[0]?.endsWith('-lng');
          if (
            !isMulti
            || fields.length === 0
            || (!firstFieldIsLat && !firstFieldIsLng)
          ) {
            fieldAcc.push({ fieldId, title: `${configProps.title} - GPS Location` });
          }
          if (!isMulti || fields.length === 0 || firstFieldIsLat) {
            fieldAcc.push({ fieldId: `${fieldId}-lat`, title: `${configProps.title} - Latitude` });
          }
          if (!isMulti || fields.length === 0 || firstFieldIsLng) {
            fieldAcc.push({ fieldId: `${fieldId}-lng`, title: `${configProps.title} - Longitude` });
          }
          return fieldAcc;
        }
        if (effectiveType === 'timeTrackingGrid') {
          if (timeTrackingGridFieldId && fieldId !== timeTrackingGridFieldId) {
            return fieldAcc;
          }
          TIME_TRACKING_GRID_EXPORT_COLUMNS.forEach((col) => {
            fieldAcc.push({
              fieldId: `${fieldId}-${col.key}`,
              title: `${configProps.title} - ${col.title}`,
            });
          });
          return fieldAcc;
        }
        if (effectiveType !== 'table') {
          /*
            Users can already set all the data (e.g. regular hours, project, cost code)
            From other columns, they dont need to use custom timecard section
          */
          if (sectionId === 'timecard') return fieldAcc;
          fieldAcc.push({ fieldId, title: configProps.title });
          return fieldAcc;
        }

        if (tableFieldId && fieldId !== tableFieldId) {
          // Can only use columns from one table
          if (configProps?.dataType === 'TimeEntry') {
            /*
              Allow picking first cost code in time entry table
              https://projectharbour.atlassian.net/browse/HARBOUR-5421
            */
            fieldAcc.push({
              fieldId: `${fieldId}-${FIRST_COST_CODE_KEY}`,
              title: `${configProps.title} - First Cost Code`,
            });
          }
          return fieldAcc;
        }
        const {
          columns = [],
        } = configProps;
        columns.forEach((col) => {
          fieldAcc.push({
            fieldId: `${fieldId}-${col.key}`,
            title: `${configProps.title} - ${col.name}`,
          });
        });
        return fieldAcc;
      }, []);
      if (exportableFields.length === 0) return acc;
      return acc.concat([{
        ...section,
        fields: exportableFields,
      }]);
    }, []);

    return exportableCustomFields;
  }, [isMulti, fields, ourFieldMap]);

  const getCustomTreeData = useCallback((exportableCustomFields, treeDataKey, treeDataTitle) => (
    [{
      key: treeDataKey,
      title: treeDataTitle,
      selectable: false,
      children: exportableCustomFields.map((section) => {
        const { fields: sectionFields = [] } = section;
        return {
          key: section.id,
          title: section.name,
          selectable: false,
          children: sectionFields.map((formField) => ({
            key: formField.fieldId,
            title: formField.title,
          })),
        };
      }),
    }]
  ), []);

  const treeData = useMemo(() => {
    const isBoardCards = type === 'boardCards';
    const userCustomSections = isMulti ? [] : userCustomFields?.sections ?? [];

    const exportableCustomFields = hasSummarize ? [] : getExportableCustomFields(ourCustomSections);
    const userExportableCustomFields = isMulti ? [] : getExportableCustomFields(userCustomSections);

    let baseFields = isMulti ? [] : TIMECARD_FIELDS;
    if (!isMulti && hasEclipse) {
      baseFields = baseFields.concat([{
        key: 'custom',
        title: 'Eclipse Fields',
        selectable: false,
        children: [{
          key: 'payType',
          title: 'Pay Type',
        }],
      }]);
    }
    const customFieldTreeData = (!exportableCustomFields?.length)
      ? []
      : getCustomTreeData(exportableCustomFields, 'custom', 'Custom Fields');

    const userCustomFieldTreeData = (!userExportableCustomFields?.length) || isBoardCards
      ? []
      : getCustomTreeData(userExportableCustomFields, 'userCustom', 'User Custom Fields');

    return baseFields.concat(customFieldTreeData).concat(userCustomFieldTreeData);
  }, [
    ourCustomSections,
    userCustomFields,
    hasEclipse,
    tableFieldId,
    timeTrackingGridFieldId,
    getExportableCustomFields,
    getCustomTreeData,
    isMulti,
    hasSummarize,
  ]);

  const costcodeOptions = useMemo(() => (
    globalCostcodes
      .filter((cc) => cc.canClockIn)
      .map((cc) => ({
        label: `${cc.code} - ${cc.name}`,
        value: cc.code,
      }))
  ), [globalCostcodes]);

  const isDateType = (fieldValue) => {
    if (!fieldValue) return false;

    // Check if it's a native board card date field
    if (type === 'boardCards') {
      if (fieldValue === 'createdAt' || fieldValue === 'lastUpdated') {
        return true;
      }
    }

    const coreId = fieldValue.replace(/-(lat|lng)$/, '');
    const meta = ourFieldMap?.[coreId];
    return meta?.selectedType === 'date'
      || meta?.selectedType === 'dateTime'
      || meta?.selectedType === 'date/time'
      || meta?.selectedType === 'Date/Time';
  };

  const isCustomDateField = useMemo(() => isDateType(field), [field, ourFieldMap]);

  return (
    <Draggable draggableId={id} type="export-column" index={orderIndex}>
      {({ draggableProps, dragHandleProps, innerRef }) => (
        <div
          className="export-column"
          {...draggableProps} // eslint-disable-line react/jsx-props-no-spreading
          ref={innerRef}
        >
          <Row justify="space-between" gutter={10}>
            <Col flex="25px">
              <DragOutlined
                {...dragHandleProps} // eslint-disable-line react/jsx-props-no-spreading
              />
            </Col>
            <Col flex="auto" style={{ maxWidth: 'calc(100% - 60px' }}>
              <OnTraccrTextInput
                placeholder="Enter Title"
                onChange={onColumnTitleChanged}
                value={title}
              />
              <OnTraccrCheckbox
                style={{ marginTop: 5 }}
                label="Multi-Field Column"
                value={isMulti}
                onChange={onMultiChange}
              />
              <TreeSelect
                multiple={isMulti}
                style={{ width: '100%', marginTop: 5 }}
                treeData={treeData}
                placeholder={isMulti ? 'Select Fields' : 'Select Field'}
                dropdownMatchSelectWidth={false}
                onChange={
                  isMulti
                    ? onFieldsChange
                    : (value) => {
                      const change = { field: value };
                      if (isDateType(value)) change.isCustomDateField = true;
                      onChange(id, change);
                    }

                }
                value={isMulti ? fields : field}
                treeDefaultExpandAll
                showSearch
                treeNodeFilterProp="title"
              />
              {!isMulti && (
                <>
                  {
                    (FIELDS_WITH_FILTERS.has(field) || FIELDS_WITH_CONFIG.has(field)
                      || isCustomDateField)
                    && (
                      <Select
                        style={{ width: '100%', marginTop: 5 }}
                        value={filterValue}
                        options={
                          isCustomDateField
                            ? CONFIG_MAP.date.options
                            : (CONFIG_MAP[field]?.options ?? costcodeOptions)
                        }
                        onSelect={onDataChange('filterValue')}
                        showSearch
                        optionFilterProp="label"
                        defaultValue={
                          isCustomDateField
                            ? CONFIG_MAP.date.default
                            : CONFIG_MAP[field]?.default
                        }
                      />
                    )
                  }
                  {
                    FIELDS_WITH_TEXT.has(field)
                    && (
                      <OnTraccrTextInput
                        style={{ width: '100%', marginTop: 5 }}
                        value={filterValue}
                        onChange={onTextChanged}
                      />
                    )
                  }
                </>
              )}
              {
                ((isMulti ? fields.length > 0 : !!field)
                  && field !== 'empty'
                  && field !== 'staticText')
                && (
                  <OnTraccrCheckbox
                    style={{ marginTop: 5 }}
                    label="Show Sub-Total"
                    value={!!subTotal}
                    onChange={(checked) => onChange(id, { subTotal: checked })}
                  />
                )
              }
            </Col>
            <Col flex="25px">
              <BorderlessButton
                iconNode={<DeleteOutlined style={{ color: colors.ONTRACCR_RED, marginLeft: 0 }} />}
                onClick={onDeleteClicked}
                style={{
                  height: 20,
                  padding: 0,
                }}
              />
            </Col>
          </Row>
        </div>
      )}
    </Draggable>
  );
}

ExportsColumn.propTypes = {
  column: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  orderIndex: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  divisionId: PropTypes.string,
  tableFieldId: PropTypes.string,
  timeTrackingGridFieldId: PropTypes.string,
  hasEclipse: PropTypes.bool,
  type: PropTypes.string.isRequired,
  formTemplateId: PropTypes.string,
  boardCardTemplateId: PropTypes.string,
  hasSummarize: PropTypes.bool,
};

ExportsColumn.defaultProps = {
  divisionId: null,
  tableFieldId: null,
  timeTrackingGridFieldId: null,
  hasEclipse: false,
  formTemplateId: null,
  boardCardTemplateId: null,
  hasSummarize: false,
};

export default ExportsColumn;
